// ─── Public Status Page — date / time / number formatting ────────────
//
// EVERY function here formats in the VISITOR'S OWN regional settings. That is
// the project rule (CLAUDE.md, "Dates, Times & Numbers"), and it matters more
// here than anywhere else in PCP: this page is read by customers on their own
// devices, and "05/08/2026" vs "05.08.2026" vs "2026-08-05" is precisely the
// kind of ambiguity that turns an outage notice into a support ticket.
//
// The page's WORDS are Danish (see ../i18n/da.ts); its NUMBERS and DATES are
// the reader's. Never hardcode 'da-DK' — not even here, where the copy is
// Danish, because the reader's device is the authority on how a date is read.
//
// ── Why NOT `undefined` ──
// Passing `undefined` is the obvious reading of "use the browser's settings",
// and it is WRONG. `undefined` means "the host's DEFAULT locale", which Chrome
// derives from the browser's UI language — not from the language preferences
// the visitor actually stated. A Dane running an English Chrome reports
//
//     navigator.languages      ["da", "en-US", "en"]     ← what they asked for
//     Intl…resolvedOptions()   "en-US"                   ← what `undefined` picks
//
// and got "Jul 20, 2026, 12:27 PM" and "99.98%" on a Danish page. Every helper
// therefore passes readerLocale() — navigator.languages — and Intl picks the
// first tag it supports, exactly as content negotiation intends.

// readerLocale() lives in ../../lib/locale.ts — ONE implementation shared with
// the authenticated app, so the two can never drift. The long "why not
// undefined" explanation lives there.
import { readerLocale } from '../../lib/locale';

/** Parse an API timestamp defensively — a malformed value renders as "—", never NaN. */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** What every formatter renders for a missing/unparseable value. */
export const EMPTY_VALUE = '—';

/** e.g. "5. aug. 2026" / "Aug 5, 2026" — depending on the reader's locale. */
export function fmtDate(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? date.toLocaleDateString(readerLocale(), { dateStyle: 'medium' }) : EMPTY_VALUE;
}

/** Date + clock time, medium/short — the default stamp for incidents and updates. */
export function fmtDateTime(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? date.toLocaleString(readerLocale(), { dateStyle: 'medium', timeStyle: 'short' }) : EMPTY_VALUE;
}

/** Clock time only — used when the date is already established by a heading. */
export function fmtTime(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? date.toLocaleTimeString(readerLocale(), { timeStyle: 'short' }) : EMPTY_VALUE;
}

/** Weekday + day + month, for a history cell's accessible name and tooltip. */
export function fmtDayLabel(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date
    ? date.toLocaleDateString(readerLocale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : EMPTY_VALUE;
}

const RELATIVE_STEPS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
];

/**
 * "for 2 minutter siden" / "2 minutes ago" — in the READER's language, via
 * Intl.RelativeTimeFormat on readerLocale(). Falls back to the absolute
 * timestamp if the engine has no RelativeTimeFormat.
 */
export function fmtRelative(
  value: string | number | Date | null | undefined,
  now: number = Date.now(),
): string {
  const date = toDate(value);
  if (!date) return EMPTY_VALUE;
  const diff = date.getTime() - now;
  try {
    const rtf = new Intl.RelativeTimeFormat(readerLocale(), { numeric: 'auto' });
    const abs = Math.abs(diff);
    for (const step of RELATIVE_STEPS) {
      if (abs >= step.ms) return rtf.format(Math.round(diff / step.ms), step.unit);
    }
    return rtf.format(Math.round(diff / 1000), 'second');
  } catch {
    return fmtDateTime(date);
  }
}

/**
 * A 0..1 ratio as a percentage in the reader's number format, e.g. "99,86 %".
 * `null` (nothing measured) renders as the em dash rather than a fake 0 %.
 */
export function fmtPercent(ratio: number | null | undefined, fractionDigits = 2): string {
  if (ratio == null || !Number.isFinite(ratio)) return EMPTY_VALUE;
  try {
    return new Intl.NumberFormat(readerLocale(), {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(ratio);
  } catch {
    return `${(ratio * 100).toFixed(fractionDigits)} %`;
  }
}

/** A plain count in the reader's number format (thousands separators included). */
export function fmtNumber(value: number | null | undefined, maximumFractionDigits = 0): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  return value.toLocaleString(readerLocale(), { maximumFractionDigits });
}

/**
 * The LOCAL calendar day at `index` of a history string.
 *
 * `historyStartDate` is a plain YYYY-MM-DD, and `new Date('2026-05-08')` parses
 * as UTC midnight — which is the PREVIOUS day for anyone west of Greenwich, so
 * every history cell would be labelled one day off there. Build a local date
 * from the parts instead, and let the Date constructor normalise the overflow
 * (day 32 of May → 1 June), which also carries DST correctly.
 */
export function dayFromHistoryIndex(historyStartDate: string, index: number): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(historyStartDate ?? '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day + index);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Elapsed time between two instants as a compact "2 t 14 min"-style string —
 * used for an incident's duration. Built from Intl.NumberFormat units so the
 * unit names come from the reader's locale too.
 */
export function fmtDuration(from: string | Date | null | undefined, to: string | Date | null | undefined): string {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return EMPTY_VALUE;
  const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  const unit = (value: number, u: 'day' | 'hour' | 'minute') => {
    try {
      return new Intl.NumberFormat(readerLocale(), { style: 'unit', unit: u, unitDisplay: 'short' }).format(value);
    } catch {
      return `${value.toLocaleString(readerLocale())}`;
    }
  };
  if (days > 0) parts.push(unit(days, 'day'));
  if (hours > 0) parts.push(unit(hours, 'hour'));
  if (minutes > 0 || parts.length === 0) parts.push(unit(minutes, 'minute'));
  return parts.join(' ');
}
